/**
 * Trunnion AI - Chat Message List
 * Scrollable message list with auto-scroll.
 */

import { useEffect, useRef } from "react";
import { MessageCircle } from "lucide-react";
import { ChatMessageBubble } from "./ChatMessage";
import { useChatStore } from "@/stores/chatStore";
import type { ChatMessage } from "@/types/chat.types";

interface ChatMessageListProps {
  messages: ChatMessage[];
}

export function ChatMessageList({ messages }: ChatMessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { isStreaming, streamingContent } = useChatStore();

  // Auto-scroll to bottom on new messages or streaming content
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, streamingContent]);

  if (messages.length === 0 && !isStreaming) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center text-trunnion-slate-500">
        <MessageCircle className="h-12 w-12 mb-3 opacity-30" />
        <p className="text-sm font-medium">Start a conversation</p>
        <p className="text-xs mt-1">
          Send a message to interact with this agent
        </p>
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
      {messages.map((msg) => (
        <ChatMessageBubble key={msg.id} message={msg} />
      ))}

      {/* Streaming indicator */}
      {isStreaming && streamingContent && (
        <div className="flex gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/20">
            <div className="h-2 w-2 rounded-full bg-blue-400 animate-pulse" />
          </div>
          <div className="max-w-[75%]">
            <div className="rounded-xl bg-trunnion-slate-800 border border-trunnion-slate-700 px-4 py-3 text-sm text-trunnion-slate-200">
              <div className="whitespace-pre-wrap">{streamingContent}</div>
              <span className="inline-block w-1.5 h-4 bg-trunnion-gold-500 animate-pulse ml-0.5 align-middle" />
            </div>
          </div>
        </div>
      )}

      {isStreaming && !streamingContent && (
        <div className="flex gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/20">
            <div className="h-2 w-2 rounded-full bg-blue-400 animate-pulse" />
          </div>
          <div className="rounded-xl bg-trunnion-slate-800 border border-trunnion-slate-700 px-4 py-3">
            <div className="flex gap-1">
              <span
                className="h-2 w-2 rounded-full bg-trunnion-slate-500 animate-bounce"
                style={{ animationDelay: "0ms" }}
              />
              <span
                className="h-2 w-2 rounded-full bg-trunnion-slate-500 animate-bounce"
                style={{ animationDelay: "150ms" }}
              />
              <span
                className="h-2 w-2 rounded-full bg-trunnion-slate-500 animate-bounce"
                style={{ animationDelay: "300ms" }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
